<template>
  <SelectionCalendarYear
    :label="yearLabel"
    :left-arrow-disabled="false"
    :right-arrow-disabled="false"
    @leftArrowPressed="moveToPreviousYear"
    @rightArrowPressed="moveToNextYear"
  />
  <DatePicker
    ref="picker"
    v-model.range="range"
    mode="date"
    show-iso-weeknumbers
    :columns="2"
    :attr="attributes"
    :max-page="maxPage"
    :max-date="maxDate"
    :locale="locale"
    :masks="masks"
    :show-navigate="'false'"
    :leftArrowDisabled="false"
    :rightArrowDisabled="true"
    :is-required="true"
    @update:fromPage="updateFromPage"
    @update:toPage="updateToPage"
    @update:model-value="updateValue"
    @update:pages="updatePages"
    @drag="updateDrag"
  />
</template>

<script lang="ts" setup>
import { DatePicker } from 'v-calendar'
import { ref, computed, watch } from 'vue'
import { addMonths, format, subDays } from 'date-fns'
import SelectionCalendarYear from './SelectionCalendarYear.vue'
import { useI18n } from 'vue-i18n'

const maxPage = computed(() => {
  const today = new Date()
  return new Date(addMonths(today, 1))
})

const maxDate = computed(() => {
  return new Date(subDays(new Date(), 1))
})

type Props = {
  startDate?: string | number
  endDate?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  startDate: undefined,
  endDate: undefined
})

const { locale } = useI18n()
const masks = {
  title: 'MMMM',
  weekdays: 'WWW'
}

type Emits = {
  (e: 'dateChanged', value: { start?: string; end?: string }): void
  (e: 'dragChanged', value: { start?: string; end?: string }): void
}

const emits = defineEmits<Emits>()

const yearLabel = ref(format(new Date(), 'yyyy').toString())

const picker = ref()
const range = ref({
  start: props.startDate ? new Date(props.startDate) : undefined,
  end: props.endDate ? new Date(props.endDate) : undefined
})

const formatting = 'yyyy-MM-dd'
const attributes = {
  key: 'selection',
  highlight: {
    start: {
      fillMode: 'solid',
      style: {
        background: '#25CED9'
      },
      contentStyle: {
        color: 'black'
      }
    },
    base: {
      fillMode: 'light',
      style: {
        background: '#C2F3EE'
      }
    },
    end: {
      fillMode: 'solid',
      style: {
        background: '#25CED9'
      },
      contentStyle: {
        color: 'black'
      }
    }
  }
}

function updatePages(pages: any[]) {
  const leftPaneYear = pages[0].year
  const rightPaneYear = pages[1].year
  if (leftPaneYear !== rightPaneYear) {
    yearLabel.value = `${leftPaneYear + '/' + rightPaneYear.toString().slice(-2)}`
  } else {
    yearLabel.value = `${leftPaneYear}`
  }
}

function updateDrag(drag: { start: Date; end: Date }) {
  emits('dragChanged', {
    start: format(drag.start, formatting),
    end: format(drag.end, formatting)
  })
}

watch(
  () => [props.startDate, props.endDate],
  async (values) => {
    if (!values[0] || !values[1]) return
    range.value = {
      start: new Date(values[0]),
      end: new Date(values[1])
    }
    await picker.value.move(new Date(values[0]), { position: 1 })
  }
)

function updateValue(value: { start?: string; end?: string }) {
  var start, end
  if (value.start) {
    start = format(new Date(value.start), formatting)
  }
  if (value.end) {
    end = format(new Date(value.end), formatting)
  }
  emits('dateChanged', { start, end })
}

async function updateFromPage() {
  if (picker.value.canMovePrev) {
    await picker.value.movePrev()
  }
}

async function updateToPage() {
  if (picker.value.canMovePrev) {
    await picker.value.moveNext()
  }
}

async function moveToPreviousYear() {
  await picker.value.moveBy(-12)
}

async function moveToNextYear() {
  await picker.value.moveBy(12)
}
</script>

<style lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';

/* Replace blue with sky blue palette */
.vc-blue {
  --vc-accent-200: #c2f3ee !important; // in between
  --vc-accent-600: #25ced9 !important; // Fillcolor
  --vc-focus-ring: 'transparent' !important;
}

.vc-container.vc-monthly.vc-blue.vc-light.vc-bordered {
  border: none;
}
.vc-weeknumber-content {
  font-size: 12px;
}

button.vc-arrow.vc-next,
button.vc-arrow.vc-prev {
  background: transparent;
  border: none;
  color: var(--gray-800);
  &:hover {
    color: var(--gray-800);
  }
}
.vc-weekday {
  @include font-mixins.desktop-regular-tiny;
  &:first-letter {
    text-transform: uppercase;
  }
}
.vc-day {
  @include font-mixins.desktop-regular-label;
  height: 40px;
  width: 40px;
}

.vc-highlight-content-outline {
  color: black;
  font-weight: 300;
}
.vc-day-content.vc-focusable.vc-focus.vc-attr.vc-attr.vc-highlight-content-light.vc-blue {
  font-weight: 300;
  color: var(--gray-800);
}
.vc-day-content.vc-focusable.vc-focus.vc-attr.vc-attr.vc-highlight-content-solid {
  color: var(--gray-800);
}
.vc-header {
  .vc-title {
    @include font-mixins.desktop-regular-paragraph;
    text-transform: capitalize;
    pointer-events: none;
    background-color: transparent;
    cursor: default;
    padding: 0;
    color: var(--gray-800);
  }
  .vc-arrow {
    text-transform: capitalize;
    @include font-mixins.desktop-regular-paragraph;
    color: variables.$color-primary;
    svg {
      width: 16px;
      height: 16px;
      color: variables.$color-primary;
    }
    .vc-next,
    .vc-prev {
      background: 'green';
    }
  }
}

.vc-pane {
  color: var(--gray-800);
}
.vc-container {
  color: var(--vc-gray-700);
}
</style>
